import React from "react";

import { useInvoiceDocuments } from "features/BranchDetails/hooks/useInvoiceDocuments";
import { useIsSuperUser } from "shared/hooks/useIsSuperUser";
import { formatDate } from "shared/lib/dateTime";
import { formatPrice } from "shared/lib/price";
import { Banner } from "shared/ui/Banner";
import { Button } from "shared/ui/Button";
import { ConfirmDialog } from "shared/ui/ConfirmDialog";
import { DateField } from "shared/ui/DateField";
import { EmptyState } from "shared/ui/EmptyState";
import { LabeledField } from "shared/ui/LabeledField";
import { NotificationSnackbar } from "shared/ui/NotificationSnackbar";
import { useNotificationSnackbar } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";
import { PageHeaderActions } from "shared/ui/PageHeaderActions";
import { Pagination } from "shared/ui/Pagination";
import { Select } from "shared/ui/Select";
import { Skeleton } from "shared/ui/Skeleton";

import { Tabs } from "shared/ui/Tabs";

import { PaymentsTable } from "./components/PaymentsTable";
import { PayoutDialog } from "./components/PayoutDialog";
import { PayoutsTable } from "./components/PayoutsTable";
import { PaymentsTab, TABS } from "./constants";
import { usePartnerPayouts } from "./hooks/usePartnerPayouts";
import { usePaymentsState } from "./hooks/usePaymentsState";
import {
    Filters,
    MutedLine,
    TabContent,
    TotalAmount,
    TotalCard,
    TotalLabel,
    Totals,
} from "./styles";

const DEFAULT_CURRENCY_ID = "RUB";

export const Payments = () => {
    const isSuperUser = useIsSuperUser();
    const notify = useNotificationSnackbar();
    const state = usePaymentsState(isSuperUser);
    const payouts = usePartnerPayouts(state.fromTime, state.toTime, state.partnerId, notify);
    const invoiceDocuments = useInvoiceDocuments(notify, state.onInvoiceUpdated);

    const paymentTotals = state.data?.totals ?? [];
    const payoutTotals = payouts.payouts?.totals ?? [];
    const payoutItems = payouts.payouts?.items ?? [];

    // Выплата бывает и в периоде без оплат, поэтому валюты собираем из обоих ответов.
    const currencies = Array.from(
        new Set([...paymentTotals, ...payoutTotals].map(({ currencyId }) => currencyId))
    );

    const selectedPartner = state.partners.find(({ id }) => id === state.partnerId);

    const partnerOptions = [
        { value: "", label: "Все партнёры" },
        ...state.partners.map(({ id, name }) => ({ value: id, label: name })),
    ];

    const renderTotals = (currencyId: string) => {
        const payment = paymentTotals.find((total) => total.currencyId === currencyId);
        const payout = payoutTotals.find((total) => total.currencyId === currencyId);
        const remainingAmount = payout?.remainingAmount ?? null;

        return (
            <Totals key={currencyId}>
                <TotalCard>
                    <TotalLabel>Сумма оплат</TotalLabel>
                    <TotalAmount>{formatPrice(payment?.totalAmount ?? 0, currencyId)}</TotalAmount>
                    <MutedLine>
                        Оплат: {payment?.count ?? 0} · картой{" "}
                        {formatPrice(payment?.cardAmount ?? 0, currencyId)} · по счетам{" "}
                        {formatPrice(payment?.invoiceAmount ?? 0, currencyId)}
                    </MutedLine>
                </TotalCard>

                <TotalCard $accent>
                    <TotalLabel>Вознаграждение</TotalLabel>
                    <TotalAmount>{formatPrice(payment?.commissionAmount ?? 0, currencyId)}</TotalAmount>
                    <MutedLine>За выбранный период</MutedLine>
                </TotalCard>

                <TotalCard>
                    <TotalLabel>Выплачено</TotalLabel>
                    <TotalAmount>{formatPrice(payout?.paidAmount ?? 0, currencyId)}</TotalAmount>
                    <MutedLine>
                        {remainingAmount === null
                            ? "За выбранный период"
                            : `Остаток к выплате за всё время: ${formatPrice(remainingAmount, currencyId)}`}
                    </MutedLine>
                </TotalCard>
            </Totals>
        );
    };

    const renderPayments = () => {
        if (state.data.total === 0) return <EmptyState title="За этот период платежей нет" />;

        return (
            <PaymentsTable
                items={state.data.items}
                busyInvoiceId={invoiceDocuments.busyInvoiceId}
                onOpenInvoice={invoiceDocuments.onOpenInvoice}
                onOpenAct={invoiceDocuments.onOpenAct}
                footer={
                    <Pagination
                        page={state.page}
                        pageSize={state.pageSize}
                        total={state.data.total}
                        onChange={state.onChangePage}
                    />
                }
            />
        );
    };

    const renderPayouts = () => {
        if (payoutItems.length === 0) return <EmptyState title="За этот период выплат нет" />;

        return (
            <PayoutsTable
                items={payoutItems}
                canManage={isSuperUser}
                showPartner={isSuperUser}
                onDelete={payouts.onStartDeleting}
            />
        );
    };

    const renderBody = () => {
        if (state.error) {
            return (
                <Banner tone="danger" title="Не удалось загрузить платежи">
                    <Button size="small" variant="outlined" color="neutral" onClick={state.onRetry}>
                        Повторить
                    </Button>
                </Banner>
            );
        }

        if (!state.data) return <Skeleton height="320px" />;

        return (
            <>
                {currencies.map(renderTotals)}

                <Tabs<PaymentsTab> value={state.tab} tabs={TABS} onChange={state.onChangeTab} />

                <TabContent>{state.tab === "payouts" ? renderPayouts() : renderPayments()}</TabContent>
            </>
        );
    };

    return (
        <>
            {selectedPartner && (
                <PageHeaderActions>
                    <Button onClick={payouts.onStartCreating}>Зафиксировать выплату</Button>
                </PageHeaderActions>
            )}

            <Filters $withPartner={isSuperUser}>
                <LabeledField label="С" htmlFor="paymentsFrom">
                    <DateField id="paymentsFrom" value={state.from} onChange={state.onChangeFrom} max={state.to} />
                </LabeledField>

                <LabeledField label="По" htmlFor="paymentsTo">
                    <DateField id="paymentsTo" value={state.to} onChange={state.onChangeTo} min={state.from} />
                </LabeledField>

                {isSuperUser && (
                    <LabeledField label="Партнёр">
                        <Select
                            value={state.partnerId ?? ""}
                            options={partnerOptions}
                            title="Партнёр"
                            onChange={state.onChangePartner}
                        />
                    </LabeledField>
                )}
            </Filters>

            {renderBody()}

            {payouts.isCreating && selectedPartner && (
                <PayoutDialog
                    partnerId={selectedPartner.id}
                    partnerName={selectedPartner.name}
                    currencies={currencies.length > 0 ? currencies : [DEFAULT_CURRENCY_ID]}
                    saving={payouts.saving}
                    onClose={payouts.onCancelCreating}
                    onSubmit={payouts.onCreate}
                />
            )}

            {payouts.deleting && (
                <ConfirmDialog
                    open
                    title="Удалить выплату?"
                    description={`Запись на ${formatPrice(
                        payouts.deleting.amount,
                        payouts.deleting.currencyId
                    )} от ${formatDate(payouts.deleting.paidAt)} исчезнет, остаток к выплате вырастет.`}
                    confirmLabel="Удалить"
                    tone="danger"
                    saving={payouts.saving}
                    onClose={payouts.onCancelDeleting}
                    onConfirm={payouts.onConfirmDeleting}
                />
            )}

            <NotificationSnackbar {...notify.snackbarProps} />
        </>
    );
};
